import { useEffect, useRef } from 'react';
import { useAuthStore, useProcessingStagesStore } from '../store';
import type { Video } from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const STAGE_LABELS: Record<string, string> = {
  COMPLIANCE_CHECK: 'Scanning for violations',
  SCORING: 'Calculating your score',
};

interface VideoStatusEvent {
  videoId: string;
  tenantId: string;
  status: 'PROCESSING' | 'SCORED' | 'FLAGGED' | 'FAILED';
  stage?: string;
  title?: string;
  hasHardBlocks?: boolean;
  hasErrors?: boolean;
  error?: string;
}

/**
 * SSE-based hook that listens for real-time video processing updates.
 *
 * Opens a single EventSource connection to GET /videos/events.
 * When the server pushes an intermediate stage event it updates the
 * processingStages map in the global Zustand store (survives navigation).
 * When processing completes (scored/flagged/failed) it fires the onRefresh
 * callback and shows a toast.
 *
 * Usage:
 *   const { processingStages } = useProcessingPoller(videos, fetchVideos);
 *   // processingStages[videoId] → "Scanning for violations" | "Calculating your score" | undefined
 */
export function useProcessingPoller(
  videos: Pick<Video, 'id' | 'scoringStatus' | 'title'>[],
  onRefresh: () => void,
): { processingStages: Record<string, string> } {
  const eventSourceRef = useRef<EventSource | null>(null);
  const videosRef = useRef(videos);
  videosRef.current = videos;

  const { processingStages, setProcessingStage, clearProcessingStage } = useProcessingStagesStore();

  const hasProcessing = videos.some(
    (v) => v.scoringStatus === 'PROCESSING' || v.scoringStatus === 'PENDING',
  );

  useEffect(() => {
    if (!hasProcessing) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      return;
    }

    const { accessToken } = useAuthStore.getState();
    if (!accessToken) return;

    if (eventSourceRef.current) return;

    const url = `${API_URL}/videos/events?token=${encodeURIComponent(accessToken)}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      try {
        const data: VideoStatusEvent = JSON.parse(event.data);
        if (data.status === 'PROCESSING' && data.stage) {
          const label = STAGE_LABELS[data.stage] ?? data.stage;
          setProcessingStage(data.videoId, label);
          return;
        }

        // Terminal event — clear stage and refresh
        clearProcessingStage(data.videoId);

        if (data.status === 'SCORED' || data.status === 'FLAGGED' || data.status === 'FAILED') {
          onRefresh();
        }
      } catch {
        // Ignore malformed events
      }
    };

    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED) {
        eventSourceRef.current = null;
      }
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasProcessing, onRefresh]);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  return { processingStages };
}
